import { Link } from 'react-router-dom';

interface FooterProps {
  onOpenService: (title: string) => void;
  onOpenIndustries: () => void;
  onOpenHowItWorks: () => void;
}

export function Footer({ onOpenService, onOpenIndustries, onOpenHowItWorks }: FooterProps) {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Services with dedicated pages
  const servicesWithPages = [
    { title: 'AI Chatbot for small businesses', path: '/services/ai-chatbot' },
    { title: 'Phone answering service', path: '/ai-phone-answering-service' },
    { title: 'Appointment scheduling', path: '/ai-appointment-scheduling' },
    { title: 'AI Receptionist', path: '/services/ai-receptionist' },
    { title: 'AI Lead follow-up', path: '/services/ai-lead-follow-up' },
    { title: 'Automated customer service', path: '/services/automated-customer-service' },
    { title: 'Marketing automation', path: '/services/marketing-automation-small-business' },
    { title: 'AI automation for small businesses', path: '/services/ai-automation-small-business' },
  ];

  // Services that open modals
  const servicesWithModals: string[] = [];

  return (
    <footer id="contact" className="bg-secondary text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div 
          className="w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `url('/readdy-assets/footer-pattern-001.jpg')`
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
          <div>
            <img
              src="/readdy-assets/static-bdb23c56967b2094c8e840b3cec5c733.png"
              alt="Dino Nuggets Logo"
              className="h-12 w-auto mb-6"
            />
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Big business technology solutions designed specifically for small businesses. Automate, grow, and succeed.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/profile.php?id=61580588333317"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-primary transition-colors duration-200 cursor-pointer"
                aria-label="Follow Dino Nuggets on Facebook"
              >
                <i className="ri-facebook-fill text-white text-lg" />
              </a>
              <a
                href="https://www.instagram.com/dinonuggetsai"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-primary transition-colors duration-200 cursor-pointer"
                aria-label="Follow Dino Nuggets on Instagram"
              >
                <i className="ri-instagram-line text-white text-lg" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Services</h4>
            <ul className="space-y-3">
              {servicesWithPages.map((service) => (
                <li key={service.title}>
                  <Link
                    to={service.path}
                    className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
              {servicesWithModals.map((title) => (
                <li key={title}>
                  <button
                    onClick={() => onOpenService(title)}
                    className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer text-left block"
                  >
                    {title}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Industries</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/industries/ai-for-home-services"
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                >
                  AI for Home Services
                </Link>
              </li>
              <li>
                <Link
                  to="/ai-automation-michigan"
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                >
                  AI Automation in Michigan
                </Link>
              </li>
              <li>
                <Link
                  to="/ai-for-contractors"
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                >
                  AI for Contractors
                </Link>
              </li>
              <li>
                <Link
                  to="/ai-for-roofers"
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                >
                  AI for Roofers
                </Link>
              </li>
              <li>
                <Link
                  to="/ai-for-landscapers"
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                >
                  AI for Landscapers
                </Link>
              </li>
              <li>
                <Link
                  to="/ai-for-rv-parks"
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                >
                  RV Parks &amp; Campgrounds
                </Link>
              </li>
              <li>
                <Link
                  to="/industries/ai-for-appliance-repair"
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                >
                  AI for Appliance Repair
                </Link>
              </li>
              <li>
                <Link
                  to="/industries/ai-for-painters"
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                >
                  AI for Painters
                </Link>
              </li>
              <li>
                <Link
                  to="/industries/ai-for-salons-spas"
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer"
                >
                  AI for Salons &amp; Spas
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Company</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer text-left block"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact-details')}
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer text-left block"
                >
                  Contact
                </button>
              </li>
              <li>
                <button
                  onClick={onOpenIndustries}
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer text-left block"
                >
                  Industries We Help
                </button>
              </li>
              <li>
                <button
                  onClick={onOpenHowItWorks}
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer text-left block"
                >
                  How It Works
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Resources</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('contact-details')}
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer text-left block"
                >
                  Support
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('cta')}
                  className="text-gray-400 hover:text-primary transition-colors text-sm cursor-pointer text-left block"
                >
                  Schedule a Call
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-400">
              © 2025 Dino Nuggets. All rights reserved.
            </div>

            <div className="flex gap-6 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-primary transition-colors cursor-pointer">
                Privacy Policy
              </a>
              <a href="/terms-of-service" className="text-gray-400 hover:text-primary transition-colors cursor-pointer">
                Terms of Service
              </a>
              <a href="/service-agreement" className="text-gray-400 hover:text-primary transition-colors cursor-pointer">
                Service Agreement
              </a>
              <span className="text-gray-400">Dino Nuggets</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}